// 统一的用户服务 API 模块 - 支持两种调用方式
// 1. 直接导入函数: import { getUserInfo, login } from '~/api/user'
// 2. 导入 API 对象: import userApi from '~/api/user'; userApi.getUserInfo()

import { encryptToBackendFormat } from "~/utils/crypto";

const parseUserInfoResponse = data => {
	if (typeof data !== "string") return data;

	try {
		const fixed = data.replace(/"(user_id|uid|id|socket_uid)"\s*:\s*(\d{15,})/g, '"$1":"$2"');
		return JSON.parse(fixed);
	} catch {
		return JSON.parse(data);
	}
};

// 创建统一的用户服务 API 函数集合
const createUserApiMethods = () => {
	const { $axios } = useNuxtApp();

	// 通用请求方法
	const request = async config => {
		try {
			const response = await $axios(config);
			return response;
		} catch (error) {
			console.error("API请求错误:", error);
			throw error;
		}
	};

	// 表单请求方法
	const formRequest = async config => {
		return request({
			...config,
			headers: {
				"Content-Type": "application/x-www-form-urlencoded",
				...config.headers,
			},
		});
	};

	// 处理敏感数据加密
	const processSensitiveData = data => {
		const processed = { ...data };
		if (processed.mobile) processed.mobile = encryptToBackendFormat(processed.mobile);
		if (processed.email) processed.email = encryptToBackendFormat(processed.email);
		return processed;
	};

	return {
		// ==================== 认证相关 ====================
		// 获取验证码
		getCodeApi: data =>
			formRequest({
				url: "/v1/login/app_captcha_code",
				method: "post",
				data: processSensitiveData(data),
				headers: {
					Lang: "zh_CN",
				},
			}),

		// 获取当前IP地区区号
		getIpHomeAddress: params =>
			request({
				url: "/api/comm/area",
				method: "get",
				params,
			}),

		// 用户登录
		login: data =>
			formRequest({
				url: "/v1/login/app",
				method: "post",
				data: processSensitiveData(data),
			}),

		// LINE 登录 - 获取Token
		lineCodeParse: data =>
			formRequest({
				url: "https://api.line.me/oauth2/v2.1/token",
				method: "post",
				data,
			}),

		// LINE 登录 - 验证Token
		lineTokenParse: data =>
			formRequest({
				url: "https://api.line.me/oauth2/v2.1/verify",
				method: "post",
				data,
			}),

		// ==================== 用户相关 ====================
		// 获取用户个人信息
		getUserInfo: params =>
			request({
				url: "/v1/user/information",
				method: "get",
				params,
				transformResponse: [parseUserInfoResponse],
			}),

		// 更新用户个人信息
		updateUserInformation: data =>
			formRequest({
				url: "/v1/user/information",
				method: "post",
				data,
			}),

		// 获取好友/追踪列表（type: 0 追踪）
		getUserFriendList: params =>
			request({
				url: "/v1/user/friend/list",
				method: "get",
				params,
			}),

		// 获取快速提问详情
		getQuestionInfo: params =>
			request({
				url: "/v1/quick_question/get_question_info",
				method: "get",
				params,
			}),

		// 获取档案列表（出参含 area_id 地区id）
		getProfileList: params =>
			request({
				url: "/v1/user/mess/list",
				method: "get",
				params,
			}),

		// 获取档案标签
		getMessLabel: params =>
			request({
				url: "/v1/user/mess/label",
				method: "get",
				params,
			}),

		// 新增档案（需传 area_id 地区id）
		addUserProfile: data =>
			formRequest({
				url: "/v1/user/mess",
				method: "post",
				data,
			}),

		// 修改档案（需传 area_id 地区id）
		updateUserProfile: data =>
			request({
				url: "/v1/user/mess/edit",
				method: "post",
				data,
			}),

		// 设置默认档案
		setDefaultProfile: data =>
			formRequest({
				url: "/v1/user/mess/default",
				method: "post",
				data,
			}),

		// 删除档案
		deleteUserProfile: data =>
			request({
				url: "/v1/user/mess/del",
				method: "post",
				data,
			}),

		// ==================== 元宝/支付相关 ====================
		// 获取用户元宝数据
		getUserCoin: params =>
			request({
				url: "/v1/user/coin",
				method: "get",
				params,
			}),

		// 获取充值元宝定价表
		getCoinFixList: params =>
			request({
				url: "/v1/coin/fixlist",
				method: "get",
				params,
			}),

		// 获取支付方式
		getPayList: params =>
			request({
				url: "/v1/overseas/order/channel",
				method: "get",
				params,
			}),

		// 充值元宝
		rechargeCoin: data =>
			request({
				url: "/v1/order/coin/recharge",
				method: "post",
				data,
			}),

		// 查询充值状态
		checkRecharge: data =>
			request({
				url: "/v1/order/pay/check",
				method: "post",
				data,
			}),

		// 获取铜币记录
		getCoinRecordList: params =>
			request({
				url: "/v1/coin/record",
				method: "get",
				params,
			}),

		// ==================== 优惠券相关 ====================
		// 获取优惠券列表
		getCouponList: (type, params) =>
			request({
				url: `/v1/coupon/${type}`,
				method: "get",
				params,
			}),

		// ==================== 订单相关 ====================
		// 获取订单列表
		getOrderList: params =>
			request({
				url: "/v1/order/new_list",
				method: "get",
				params,
			}),

		// 订单查询
		checkPay: data =>
			formRequest({
				url: "/api/order/list",
				method: "post",
				data,
			}),

		// 铜币支付
		coinPay: data =>
			formRequest({
				url: "/v1/user/coin/consume",
				method: "post",
				data,
			}),

		// ==================== 消息相关 ====================
		// 获取老师消息列表
		getTeacherMsgList: params =>
			request({
				url: "/v2/freechat/lists",
				method: "get",
				params,
			}),

		// 获取系统消息列表
		getSysMsgList: params =>
			request({
				url: "/api.service/msg/notice/list",
				method: "get",
				params,
			}),

		// 获取未读消息数
		getUnreadMsgCount: params =>
			request({
				url: "/api.service/msg/info",
				method: "get",
				params,
			}),

		// 更新消息已读状态
		updateMsgReadStatus: data =>
			request({
				url: "/v1/message/readOne",
				method: "post",
				data,
			}),

		// 清除未读消息（按类型批量已读）
		clearUnreadMsg: data =>
			request({
				url: "/v1/message/read",
				method: "post",
				data,
			}),

		// ==================== 其他 ====================
		// 获取新生儿专区信息
		getNewBornInfo: () =>
			request({
				url: "/v1/newborn_zone/get_list",
				method: "get",
			}),

		// 获取收货地址列表
		getAddressList: () =>
			request({
				url: "/api/address/user/list",
				method: "get",
			}),

		// 获取收货地址详情
		getAddressInfo: params =>
			request({
				url: "/api/address/user/info",
				method: "get",
				params,
			}),

		// 更新收货地址
		updateAddress: data =>
			request({
				url: "/api/address/user/edit",
				method: "post",
				data,
			}),

		// 新增收货地址
		addAddress: data =>
			request({
				url: "/api/address/user/add",
				method: "post",
				data,
			}),

		// 删除收货地址
		deleteAddress: data =>
			request({
				url: "/api/address/user/delete",
				method: "post",
				data,
			}),

		// 添加收藏
		addGoodsCollect: data =>
			request({
				url: "/v1/product_site/goods/add_collect",
				method: "post",
				data,
			}),

		// 取消收藏
		cancelGoodsCollect: data =>
			request({
				url: "/v1/product_site/goods/cancel_collect",
				method: "post",
				data,
			}),
		// 添加浏览记录
		addGoodsBrowse: data =>
			request({
				url: "/v1/product_site/goods/add_browse",
				method: "post",
				data,
			}),
	};
};

// 懒加载用户 API 实例
let userApiInstance = null;
const getUserApiInstance = () => {
	if (!userApiInstance) {
		userApiInstance = createUserApiMethods();
	}
	return userApiInstance;
};

// 导出单独的 API 函数（支持直接导入）
export const getCodeApi = data => getUserApiInstance().getCodeApi(data);
export const getIpHomeAddress = params => getUserApiInstance().getIpHomeAddress(params);
export const login = data => getUserApiInstance().login(data);
export const lineCodeParse = data => getUserApiInstance().lineCodeParse(data);
export const lineTokenParse = data => getUserApiInstance().lineTokenParse(data);
export const getUserInfo = params => getUserApiInstance().getUserInfo(params);
export const updateUserInformation = data => getUserApiInstance().updateUserInformation(data);
export const getUserFriendList = params => getUserApiInstance().getUserFriendList(params);
export const getQuestionInfo = params => getUserApiInstance().getQuestionInfo(params);
export const getProfileList = params => getUserApiInstance().getProfileList(params);
export const getMessLabel = params => getUserApiInstance().getMessLabel(params);
export const addUserProfile = data => getUserApiInstance().addUserProfile(data);
export const updateUserProfile = data => getUserApiInstance().updateUserProfile(data);
export const setDefaultProfile = data => getUserApiInstance().setDefaultProfile(data);
export const deleteUserProfile = data => getUserApiInstance().deleteUserProfile(data);
export const getUserCoin = params => getUserApiInstance().getUserCoin(params);
export const getCoinFixList = params => getUserApiInstance().getCoinFixList(params);
export const getPayList = params => getUserApiInstance().getPayList(params);
export const rechargeCoin = data => getUserApiInstance().rechargeCoin(data);
export const checkRecharge = data => getUserApiInstance().checkRecharge(data);
export const getCoinRecordList = params => getUserApiInstance().getCoinRecordList(params);
export const getCouponList = (type, params) => getUserApiInstance().getCouponList(type, params);
export const getOrderList = params => getUserApiInstance().getOrderList(params);
export const checkPay = data => getUserApiInstance().checkPay(data);
export const coinPay = data => getUserApiInstance().coinPay(data);
export const getTeacherMsgList = params => getUserApiInstance().getTeacherMsgList(params);
export const getSysMsgList = params => getUserApiInstance().getSysMsgList(params);
export const getUnreadMsgCount = params => getUserApiInstance().getUnreadMsgCount(params);
export const updateMsgReadStatus = data => getUserApiInstance().updateMsgReadStatus(data);
export const clearUnreadMsg = data => getUserApiInstance().clearUnreadMsg(data);
export const getNewBornInfo = () => getUserApiInstance().getNewBornInfo();
export const getAddressList = () => getUserApiInstance().getAddressList();
export const getAddressInfo = params => getUserApiInstance().getAddressInfo(params);
export const updateAddress = data => getUserApiInstance().updateAddress(data);
export const addAddress = data => getUserApiInstance().addAddress(data);
export const deleteAddress = data => getUserApiInstance().deleteAddress(data);
export const addGoodsCollect = data => getUserApiInstance().addGoodsCollect(data);
export const cancelGoodsCollect = data => getUserApiInstance().cancelGoodsCollect(data);
export const addGoodsBrowse = data => getUserApiInstance().addGoodsBrowse(data);

// 默认导出用户 API 对象（支持 userApi.方法名 调用）
const userApi = {
	get: () => getUserApiInstance(),
};

// 使用 Proxy 来实现 userApi.方法名 的调用方式
const userApiProxy = new Proxy(userApi, {
	get(target, prop) {
		if (prop === "get") {
			return target.get;
		}
		const instance = getUserApiInstance();
		if (prop in instance) {
			return instance[prop];
		}
		return undefined;
	},
});

export default userApiProxy;
